import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { API } from "@/App";
import { getSessionToken, clearSessionToken } from "@/auth/session";

const PipelineContext = createContext(null);

export function PipelineProvider({ children, sessionToken, userId }) {
  const [pipeline, setPipeline] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const [morningBrief, setMorningBrief] = useState(null);
  const active = useRef(true);
  const requests = useRef(new Set());
  const current = useCallback(() => active.current && userId && sessionToken && getSessionToken() === sessionToken, [sessionToken, userId]);

  const request = useCallback(async (path, options = {}) => {
    if (!current()) return null;
    const controller = new AbortController();
    requests.current.add(controller);
    try {
      const response = await fetch(`${API}/pipeline${path}`, {
        ...options, signal: controller.signal,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionToken}` },
      });
      if (!current()) return null;
      if (response.status === 401) { clearSessionToken(); return null; }
      if (!response.ok) return null;
      const data = await response.json();
      return current() ? data : null;
    } catch (error) {
      if (error.name !== "AbortError") console.error("Pipeline request failed");
      return null;
    } finally { requests.current.delete(controller); }
  }, [sessionToken, current]);

  const applyPipeline = useCallback((data) => {
    if (!current() || !Array.isArray(data)) return;
    setPipeline(data);
    const updates = data.filter((item) => item.has_update);
    const phase = updates.filter((item) => item.changes?.some((change) => change.field === "phase")).length;
    const status = updates.filter((item) => item.changes?.some((change) => change.field === "status")).length;
    setUpdateCount(updates.length);
    setMorningBrief({
      total_assets: data.length, total_updates: updates.length, phase_changes: phase, status_changes: status,
      updates_this_week: updates.filter((item) => item.last_updated && Date.now() - Date.parse(item.last_updated) < 7 * 86400000).length,
      summary: updates.length ? `${updates.length} assets updated. ${phase} phase changes. ${status} status changes.` : "No updates detected in your saved pipeline.",
    });
  }, [current]);

  const fetchPipeline = useCallback(async () => {
    const data = await request("");
    if (data) applyPipeline(data);
    return data;
  }, [request, applyPipeline]);

  useEffect(() => {
    active.current = true;
    if (userId && sessionToken) fetchPipeline();
    const controllers = requests.current;
    return () => { active.current = false; controllers.forEach((controller) => controller.abort()); controllers.clear(); };
  }, [fetchPipeline, sessionToken, userId]);

  const addToPipeline = useCallback(async (assetName) => {
    if (!current()) return { success: false, error: "Authentication required" };
    setIsLoading(true);
    try {
      const data = await request("/add", { method: "POST", body: JSON.stringify({ asset_name: assetName }) });
      if (!data || data.success === false) return { success: false, error: data?.message || "Unable to add asset" };
      await fetchPipeline();
      return { success: true, item: data };
    } finally { if (current()) setIsLoading(false); }
  }, [current, request, fetchPipeline]);
  const removeFromPipeline = useCallback(async (assetId) => {
    const data = await request(`/${encodeURIComponent(assetId)}`, { method: "DELETE" });
    if (!data) return { success: false };
    if (current()) setPipeline((previous) => previous.filter((item) => item.asset_id !== assetId));
    return { success: true };
  }, [request, current]);
  const refreshPipeline = useCallback(async () => {
    if (!current()) return { success: false };
    setIsLoading(true);
    try {
      const data = await request("/refresh", { method: "POST" });
      if (!data) return { success: false };
      if (Array.isArray(data)) applyPipeline(data); else await fetchPipeline();
      return { success: true };
    } finally { if (current()) setIsLoading(false); }
  }, [current, request, applyPipeline, fetchPipeline]);
  const markUpdatesSeen = useCallback(async () => {
    const data = await request("/mark-seen", { method: "POST" });
    if (data && current()) { setPipeline((previous) => previous.map((item) => ({ ...item, has_update: false }))); setUpdateCount(0); }
  }, [request, current]);
  const isInPipeline = useCallback((name) => pipeline.some((item) => item.asset_name?.toLowerCase() === name?.toLowerCase()), [pipeline]);
  const getPipelineItem = useCallback((name) => pipeline.find((item) => item.asset_name?.toLowerCase() === name?.toLowerCase()), [pipeline]);
  return <PipelineContext.Provider value={{
    pipeline, isLoading, updateCount, morningBrief, addToPipeline, removeFromPipeline, isInPipeline,
    getPipelineItem, refreshPipeline, fetchPipeline, markUpdatesSeen,
    watchlist: pipeline, addToWatchlist: addToPipeline, removeFromWatchlist: removeFromPipeline,
    isInWatchlist: isInPipeline, getWatchlistItem: getPipelineItem, refreshWatchlist: refreshPipeline, fetchWatchlist: fetchPipeline,
  }}>{children}</PipelineContext.Provider>;
}
export function usePipeline() {
  const context = useContext(PipelineContext);
  if (!context) throw new Error("usePipeline must be used within an authenticated PipelineProvider");
  return context;
}
export const useWatchlist = usePipeline;
export const WatchlistProvider = PipelineProvider;